'use client'

import React, { createContext, useCallback, useContext, useState } from 'react'

import { ChevronDown, Download } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { Thumbnail } from '@/components/common/Attachment/Thumbnail'

import { getFileExtensionFromName } from '@/helpers/file'

// ============================================================================
// Context
// ============================================================================

interface FileCardContextValue {
  isOpen: boolean
  toggle: () => void
}

const FileCardContext = createContext<FileCardContextValue | null>(null)

function useFileCardContext() {
  const context = useContext(FileCardContext)
  if (!context) {
    throw new Error('FileCardV2.* components must be used within a FileCardV2')
  }
  return context
}

// ============================================================================
// Types
// ============================================================================

interface FileCardV2Props {
  children: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

interface FileCardTriggerProps {
  children: React.ReactNode
  className?: string
}

interface FileCardIconProps {
  filename: string
  className?: string
}

interface FileCardTitleProps {
  children: React.ReactNode
  title?: string
  className?: string
}

interface FileCardMetaProps {
  children: React.ReactNode
  className?: string
}

interface FileCardActionsProps {
  children: React.ReactNode
  className?: string
}

interface FileCardContentProps {
  children: React.ReactNode
  className?: string
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * FileCardV2.Trigger - Clickable header area
 *
 * Automatically extracts FileCardV2.Actions from children and places them
 * in the right section next to the chevron for proper layout.
 */
const Trigger: React.FC<FileCardTriggerProps> = ({ children, className }) => {
  const { isOpen, toggle } = useFileCardContext()

  // Separate Actions from other children to place them on the right
  const childArray = React.Children.toArray(children)
  const actionsChild = childArray.find(
    (child) =>
      React.isValidElement(child) &&
      (child.type as { displayName?: string })?.displayName ===
        'FileCardV2.Actions',
  )
  const otherChildren = childArray.filter((child) => child !== actionsChild)

  return (
    <div
      className={twMerge(
        'flex cursor-pointer items-center justify-between p-3',
        className,
      )}
      onClick={toggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          toggle()
        }
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        {otherChildren}
      </div>
      <div className="group/file flex shrink-0 items-center gap-2 pl-3">
        {actionsChild}
        <ChevronDown
          className={twMerge(
            'h-3.5 w-3.5 transition-transform duration-200',
            isOpen
              ? 'rotate-180 text-accent-600 dark:text-accent-400'
              : 'text-primary-400 dark:text-primary-500',
          )}
        />
      </div>
    </div>
  )
}
Trigger.displayName = 'FileCardV2.Trigger'

/**
 * FileCardV2.Icon - File type icon using the shared Thumbnail component
 */
const Icon: React.FC<FileCardIconProps> = ({ filename, className }) => {
  const extension = getFileExtensionFromName(filename)
  return (
    <Thumbnail type={extension} className={twMerge('rounded-md', className)} />
  )
}
Icon.displayName = 'FileCardV2.Icon'

/**
 * FileCardV2.Title - File name display
 */
const Title: React.FC<FileCardTitleProps> = ({
  children,
  title,
  className,
}) => {
  const { isOpen } = useFileCardContext()

  return (
    <span
      className={twMerge(
        'truncate text-[13px] font-semibold transition-colors',
        isOpen
          ? 'text-accent-900 dark:text-accent-100'
          : 'text-primary-900 dark:text-primary-100',
        className,
      )}
      title={title}
    >
      {children}
    </span>
  )
}
Title.displayName = 'FileCardV2.Title'

/**
 * FileCardV2.Meta - File metadata (type, size, date)
 */
const Meta: React.FC<FileCardMetaProps> = ({ children, className }) => {
  const visibleChildren = React.Children.toArray(children).filter((child) => {
    if (typeof child === 'string') return child.trim().length > 0
    if (typeof child === 'number') return child !== 0
    return true
  })

  return (
    <div
      className={twMerge(
        'flex items-center gap-1.5 font-mono text-xs text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {visibleChildren.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="text-primary-300 dark:text-primary-600">•</span>
          )}
          {child}
        </React.Fragment>
      ))}
    </div>
  )
}
Meta.displayName = 'FileCardV2.Meta'

/**
 * FileCardV2.Actions - Action buttons (download, etc.)
 */
const Actions: React.FC<FileCardActionsProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge('flex items-center gap-2', className)}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}
Actions.displayName = 'FileCardV2.Actions'

/**
 * FileCardV2.Content - Expandable content area
 */
const Content: React.FC<FileCardContentProps> = ({ children, className }) => {
  const { isOpen } = useFileCardContext()

  return (
    <div
      className={twMerge(
        'grid transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      )}
    >
      <div className="overflow-hidden">
        <div className={twMerge('px-3 pb-3 pt-0', className)}>{children}</div>
      </div>
    </div>
  )
}
Content.displayName = 'FileCardV2.Content'

// ============================================================================
// Convenience Components
// ============================================================================

/**
 * DownloadButton - Pre-styled download button for file cards
 */
interface DownloadButtonProps {
  onClick?: () => void
  className?: string
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
  onClick,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        'rounded-md p-2 text-primary-400 transition-all hover:bg-accent-50 hover:text-accent-600 dark:text-primary-500 dark:hover:bg-accent-900/30 dark:hover:text-accent-400',
        className,
      )}
    >
      <Download className="h-4 w-4" />
    </button>
  )
}
DownloadButton.displayName = 'FileCardV2.DownloadButton'

// ============================================================================
// Main Component
// ============================================================================

/**
 * FileCardV2 - Expandable document card with AI summary support
 *
 * Compound component for displaying file information with expandable content.
 * Supports both controlled and uncontrolled usage.
 *
 * @example
 * ```tsx
 * <FileCardV2>
 *   <FileCardV2.Trigger>
 *     <FileCardV2.Icon filename={file.name} />
 *     <div className="flex flex-col gap-0.5 overflow-hidden">
 *       <FileCardV2.Title title={file.name}>{file.name}</FileCardV2.Title>
 *       <FileCardV2.Meta>
 *         <span className="uppercase font-medium">PDF</span>
 *         <span>256 KB</span>
 *         <span>Dec 15, 2024</span>
 *       </FileCardV2.Meta>
 *     </div>
 *     <FileCardV2.Actions>
 *       <FileCardV2.DownloadButton onClick={() => download(file)} />
 *     </FileCardV2.Actions>
 *   </FileCardV2.Trigger>
 *   <FileCardV2.Content>
 *     <AISummaryBox variant="subtle">
 *       {file.summary || "No AI summary available."}
 *     </AISummaryBox>
 *   </FileCardV2.Content>
 * </FileCardV2>
 * ```
 */
const FileCardV2: React.FC<FileCardV2Props> & {
  Trigger: typeof Trigger
  Icon: typeof Icon
  Title: typeof Title
  Meta: typeof Meta
  Actions: typeof Actions
  Content: typeof Content
  DownloadButton: typeof DownloadButton
} = ({ children, defaultOpen = false, open, onOpenChange, className }) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)

  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen

  const toggle = useCallback(() => {
    const newOpen = !isOpen
    if (!isControlled) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }, [isControlled, isOpen, onOpenChange])

  return (
    <FileCardContext.Provider value={{ isOpen, toggle }}>
      <div
        className={twMerge(
          'snap-start overflow-hidden rounded-lg border transition-all duration-300',
          isOpen
            ? 'border-accent-200 bg-accent-50/30 ring-1 ring-accent-200 dark:border-accent-700 dark:bg-accent-900/20 dark:ring-accent-800'
            : 'border-primary-200 bg-white hover:border-accent-300 hover:shadow-sm dark:border-primary-700 dark:bg-primary-900 dark:hover:border-accent-600',
          className,
        )}
      >
        {children}
      </div>
    </FileCardContext.Provider>
  )
}

FileCardV2.displayName = 'FileCardV2'
FileCardV2.Trigger = Trigger
FileCardV2.Icon = Icon
FileCardV2.Title = Title
FileCardV2.Meta = Meta
FileCardV2.Actions = Actions
FileCardV2.Content = Content
FileCardV2.DownloadButton = DownloadButton

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface FileCardSkeletonProps {
  className?: string
}

/**
 * FileCardV2.Skeleton - Loading skeleton for file cards
 *
 * Matches the collapsed file card layout exactly.
 *
 * @example
 * ```tsx
 * <FileCardV2.Skeleton />
 * ```
 */
const FileCardSkeleton: React.FC<FileCardSkeletonProps> = ({ className }) => {
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-lg border border-primary-200 bg-white dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
          {/* Icon skeleton */}
          <div className="flex h-9 min-w-9 animate-pulse items-center justify-center rounded-md bg-primary-100 dark:bg-primary-800" />

          {/* Title + Meta skeleton */}
          <div className="flex flex-col gap-1.5 overflow-hidden">
            <div className="h-4 w-48 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-10 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-2 w-1 animate-pulse rounded-full bg-primary-200 dark:bg-primary-700" />
              <div className="h-2.5 w-14 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-2 w-1 animate-pulse rounded-full bg-primary-200 dark:bg-primary-700" />
              <div className="h-2.5 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            </div>
          </div>
        </div>

        {/* Actions skeleton */}
        <div className="flex shrink-0 items-center gap-2 pl-3">
          <div className="h-6 w-14 animate-pulse rounded-md bg-primary-200 dark:bg-primary-700" />
          <div className="h-3.5 w-3.5 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
        </div>
      </div>
    </div>
  )
}

FileCardSkeleton.displayName = 'FileCardV2.Skeleton'

// Attach skeleton to FileCardV2
const FileCardV2WithSkeleton = FileCardV2 as typeof FileCardV2 & {
  Skeleton: typeof FileCardSkeleton
}
FileCardV2WithSkeleton.Skeleton = FileCardSkeleton

export { FileCardV2WithSkeleton as FileCardV2 }
export type {
  FileCardV2Props,
  FileCardTriggerProps,
  FileCardIconProps,
  FileCardTitleProps,
  FileCardMetaProps,
  FileCardActionsProps,
  FileCardContentProps,
  FileCardSkeletonProps,
}
